





























































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  toRefs,
  useRouter,
  watch
} from '~/utils/nuxt3-migration'
import { NotificationEvent } from '~/models/notification/types'
import CImg from '~/components/shared/configurable/image/CImg.vue'
import CLink from '~/components/shared/configurable/link/CLink.vue'
import { ciTimes } from '~/icons/source/solid/times'
import { format } from '~/utils/date'
import CBadge from '~/components/shared/configurable/badge/CBadge.vue'
import { SIMPLE_DATETIME_FORMAT } from '~/constants/date'
import { ciMail } from '~/icons/source/regular/mail'
import { ciOpenMail } from '~/icons/source/regular/open-mail'
import { ciTrash } from '~/icons/source/regular/trash'
import { useAppConfirmationModal } from '~/compositions/app-confirmation-modal'
import { defineComponentTranslations } from '~/utils/i18n'
import { useI18n } from '~/compositions/i18n'
import NotificationService from '~/services/notification/NotificationService'
import { useDep } from '~/compositions/dependency-container'
import { useRequestError } from '~/compositions/request-error'
import CAvatar from '~/components/shared/configurable/image/avatar/CAvatar.vue'
import { formatNumber } from '~/utils/number'

export default defineComponent({
  computed: {
    CLink() {
      return CLink
    }
  },
  components: { CImg, CBadge, CAvatar },
  props: {
    notification: {
      type: Object as PropType<NotificationEvent>,
      required: true
    },
    floating: { type: Boolean, default: false },
    loading: { type: Boolean, default: false }
  },
  setup(props, { emit }) {
    const { notification, loading } = toRefs(props)
    const router = useRouter()
    const { t } = useI18n()
    const confirm = useAppConfirmationModal()
    const notificationService = useDep(NotificationService)
    const handleRequestError = useRequestError()

    const isLoading = ref(loading.value)
    const internalNotification = ref<NotificationEvent>({
      ...notification.value
    })

    const isAnnouncement = computed(
      () => internalNotification.value.isAnnouncement
    )

    const stateColor = computed(() => {
      const colors = {
        warning: 'tw-text-amber-700',
        primary: 'tw-text-primary-500',
        danger: 'tw-text-red-400',
        success: 'tw-text-green-500',
        info: 'tw-text-blue-500'
      }
      return colors[notification.value.state]
    })

    const hasNuxtRoute = computed(() => {
      const resolvedRoute = router.resolve({
        path: internalNotification.value.targetUrl
      })
      return Boolean(resolvedRoute.route?.name)
    })

    const createdDate = computed(() =>
      format(internalNotification.value.created, SIMPLE_DATETIME_FORMAT)
    )

    const isTypeGroupMessages = computed(
      () => internalNotification.value.typeGroup === 'messages'
    )

    const avatar = computed(() => {
      if (!internalNotification.value.image.includes('no_photo')) {
        return internalNotification.value.image
      }
      return null
    })

    const badgeColor = computed(() =>
      isTypeGroupMessages.value ? 'tw-bg-orange-400' : 'tw-bg-primary-500'
    )

    const pathToUse = computed(() => {
      const targetUrl = internalNotification.value.targetUrl
      if (targetUrl) {
        return `${targetUrl}?${new URLSearchParams(
          router.currentRoute.query
        )?.toString()}`
      }
      return `/${internalNotification.value.classifiedId[0]}`
    })
    const notificationHref = computed(() => {
      if (!internalNotification.value.targetUrl) {
        return undefined
      }

      return hasNuxtRoute.value || pathToUse.value.startsWith('http')
        ? pathToUse.value
        : null
    })

    const notificationTo = computed(() => {
      if (!internalNotification.value.targetUrl) {
        return undefined
      }

      return hasNuxtRoute.value || pathToUse.value.startsWith('http')
        ? null
        : {
            path: pathToUse.value
          }
    })

    const newPrice = computed(() =>
      notification.value.data
        ? `${formatNumber(notification.value.data.newPrice)}€`
        : ''
    )

    const oldPrice = computed(() =>
      notification.value.data
        ? `${formatNumber(notification.value.data.oldPrice)}€`
        : ''
    )

    const priceReduction = computed(() => {
      if (!notification.value.data) {
        return ''
      }
      const oldPrice = parseFloat(notification.value.data.oldPrice)
      const newPrice = parseFloat(notification.value.data.newPrice)

      if (!oldPrice || !newPrice) {
        return ''
      }

      const difference = oldPrice - newPrice
      const percentageChange = (difference / oldPrice) * 100

      return `${Math.abs(Math.trunc(percentageChange))}%`
    })

    watch(
      notification,
      newNotification => {
        internalNotification.value = newNotification
      },
      { deep: true }
    )

    watch(loading, newVal => (isLoading.value = newVal))

    function handleReadNotification() {
      if (!internalNotification.value.isRead) {
        emit('read-notification', internalNotification.value)
      }
    }

    function handleDeleteNotification() {
      confirm.show({
        variant: 'danger',
        title: t('delete_notification'),
        centered: true,
        text: t('delete_notification_msg'),
        confirmButtonText: t('delete'),
        size: 'md',
        onConfirm: () => deleteNotification()
      })
    }

    async function deleteNotification() {
      isLoading.value = true
      try {
        await notificationService.deleteNotification(
          internalNotification.value.id
        )
        emit('notification-deleted', internalNotification.value.id)
      } catch (error) {
        handleRequestError(error)
      } finally {
        isLoading.value = false
      }
    }

    return {
      notificationHref,
      notificationTo,
      ciTimes,
      createdDate,
      ciMail,
      ciOpenMail,
      ciTrash,
      handleReadNotification,
      handleDeleteNotification,
      isLoading,
      internalNotification,
      isTypeGroupMessages,
      avatar,
      isAnnouncement,
      stateColor,
      badgeColor,
      newPrice,
      oldPrice,
      priceReduction
    }
  },
  i18n: defineComponentTranslations({
    delete_notification: {
      en: 'Delete notification',
      el: 'Διαγραφή ειδοποίησης'
    },
    delete_notification_msg: {
      en: 'Are you sure you want to delete this notification?',
      el: 'Είσαι σίγουρος ότι θέλεις να διαγράψεις αυτή την ειδοποίηση;'
    },
    car_announcements: {
      en: 'Car.gr Announcements',
      el: 'Ανακοινώσεις Car.gr'
    },
    mark_as_read: {
      en: 'Mark as read',
      el: 'Σήμανση ως αναγνωσμένη'
    }
  })
})
